import { Separator } from "@/components/ui/separator";
import React, { useState } from "react";
import { Button } from "./ui/button";
import { useNavigate, useLocation, Link } from "react-router-dom";

import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";

function Header() {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const location = useLocation();
  const pathname = location.pathname;

  const isEnglish = pathname.startsWith("/en");
  const isDutch = pathname.startsWith("/nl");
  const lang = isEnglish ? "en" : "nl";

  const logoSrc = isEnglish
    ? "/assets/logos/ik-ben-jo_logo_en.svg"
    : "/assets/logos/ik-ben-jo_logo_nl.svg";

  const routeMap = {
    "/nl/verhaal": "/en/story",
    "/nl/links": "/en/links",
    "/nl/colofon": "/en/colophon",
    "/nl/docu": "/en/docu",
    "/nl/onderzoek": "/en/research",
    "/nl/privacystatement": "/en/privacystatement",
    "/nl": "/en",

    "/en/story": "/nl/verhaal",
    "/en/links": "/nl/links",
    "/en/colophon": "/nl/colofon",
    "/en/docu": "/nl/docu",
    "/en/research": "/nl/onderzoek",
    "/en/privacystatement": "/nl/privacystatement",
    "/en": "/nl",
  };

  const switchLanguage = (newLang) => {
    const target = routeMap[pathname];
    if (target) navigate(target);
    else navigate(`/${newLang}`);
  };

  const menu = {
    nl: [
      { label: "Kunstwerken", to: "/nl/kunstwerken" },
      { label: "Verhaal", to: "/nl/verhaal" },
      { label: "Links", to: "/nl/links" },
    ],
    en: [
      { label: "Artworks", to: "/en/artworks" },
      { label: "Story", to: "/en/story" },
      { label: "Links", to: "/en/links" },
    ],
  };

  return (
    <>
      {/* MOBILE HEADER */}
      <header className="md:hidden sticky top-0 z-50 w-full bg-[rgb(var(--background))]">
        <div className="px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center">
          {/* LEFT: Hamburger (Sheet) */}
          <div className="flex items-center justify-start">
            <Sheet
              open={isSheetOpen}
              onOpenChange={(open) => setIsSheetOpen(open)}
            >
              <SheetTrigger asChild>
                <button
                  className="pointer-events-auto hover:opacity-70 transition focus:outline-none focus:ring-0 bg-background"
                  aria-label="Open menu"
                >
                  <img
                    src="/assets/icons/menu_icon.svg"
                    alt="Menu"
                    className="w-7 h-7"
                  />
                </button>
              </SheetTrigger>

              <SheetContent
                side="left"
                className="
                  rounded-xl inset-2 bottom-2 right-2
                  h-auto w-auto
                  bg-[rgb(var(--card))]
                  text-[rgb(var(--card-foreground))]
                  flex flex-col
                "
              >
                <SheetHeader>
                  <SheetTitle className="font-gotham text-xl text-left">
                    {lang === "nl" ? "Menu" : "Menu"}
                  </SheetTitle>
                  <Separator className="my-4 bg-[#ddd] border-[1px] border-black/20" />
                </SheetHeader>

                <div className="flex-1 overflow-y-auto">
                  <ul className="space-y-4 my-2">
                    {menu[lang].map((item) => (
                      <li key={item.to}>
                        <button
                          onClick={() => {
                            navigate(item.to);
                            setIsSheetOpen(false);
                          }}
                          className="text-2xl font-gotham bg-transparent px-0 p-0 text-black hover:opacity-70 focus:outline-none focus:ring-0"
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* CENTER: Logo */}
          <div className="flex items-start justify-start">
            <Link to={`/${lang}`} className="block">
              <img
                src={logoSrc}
                alt={isEnglish ? "Ik ben Jo logo (English)" : "Ik ben Jo logo"}
                className="h-24 px-4"
              />
            </Link>
          </div>

          {/* RIGHT: Language toggle (same as desktop) */}
          <div className="flex items-center justify-end font-gotham">
            <Button
              variant="link"
              onClick={() => switchLanguage("nl")}
              className={`p-2 text-lg focus:outline-none focus:ring-0 bg-transparent text-black hover:underline hover:decoration-2 ${
                isDutch ? "font-bold" : "font-normal"
              }`}
            >
              NL
            </Button>
            <span>/</span>
            <Button
              variant="link"
              onClick={() => switchLanguage("en")}
              className={`p-2 text-lg focus:outline-none focus:ring-0 bg-transparent text-black hover:underline hover:decoration-2 ${
                isEnglish ? "font-bold" : "font-normal"
              }`}
            >
              EN
            </Button>
          </div>
        </div>
      </header>

      {/* DESKTOP HEADER (your current one) */}
      <div className="hidden md:block">
        {/* keep your existing desktop logo positioning if you want */}
        <div className="fixed top-5 left-5 z-40 items-start justify-start overflow-visible">
          <Link to={`/${lang}`}>
            <img
              src={logoSrc}
              alt={isEnglish ? "Ik ben Jo logo (English)" : "Ik ben Jo logo"}
              className="h-20 sm:h-28 px-0"
            />
          </Link>
        </div>

        <header
          className={`sticky items-top top-0 z-30 w-full p-4 grid grid-cols-[1fr_auto_1fr] bg-[rgb(var(--background))] text-[rgb(var(--foreground))] ${
            isSheetOpen ? "opacity-100 pointer-events-none" : ""
          }`}
        >
          {/* CENTER: MENU ITEMS */}
          <div className="col-start-2 flex items-start justify-center space-x-6">
            {menu[lang].map((item) => (
              <Link key={item.to} to={item.to}>
                <Button
                  variant="link"
                  className="text-lg bg-transparent font-gotham text-black hover:underline hover:decoration-2 focus:outline-none focus:ring-0"
                >
                  {item.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* RIGHT: LANGUAGE */}
          <div className="col-start-3 flex items-start justify-end font-gotham">
            <div className="flex items-center">
              <Button
                variant="link"
                onClick={() => switchLanguage("nl")}
                className={`p-2 text-lg focus:outline-none focus:ring-0 bg-transparent text-black hover:underline hover:decoration-2 ${
                  isDutch ? "font-bold" : "font-normal"
                }`}
              >
                NL
              </Button>
              <span>/</span>
              <Button
                variant="link"
                onClick={() => switchLanguage("en")}
                className={`p-2 text-lg focus:outline-none focus:ring-0 bg-transparent text-black hover:underline hover:decoration-2 ${
                  isEnglish ? "font-bold" : "font-normal"
                }`}
              >
                EN
              </Button>
            </div>
          </div>
        </header>
      </div>
    </>
  );
}

export default Header;
